// 携帯からの登録履歴（docs/37）
//
// 2026-08-28 に承認フローを廃止した（本城さん要望「承認なしで、そのままマスター登録」）。
// 携帯フォームはログイン必須になり、送信＝その場でマスターシートに登録される。
// この画面は**誰がいつ何を登録したかを後から確認するための履歴**で、操作ボタンは持たない。

import React, { useEffect, useState } from "react";
import { Ic } from "../components/Icons";
import { fetchEntryRequests, type EntryRequest } from "../lib/entryApi";
import { Spinner, SkeletonRows } from "../components/Loading";

export function EntryRequestsPanel({ onCount }: { onCount: (n: number) => void }) {
  const [items, setItems] = useState<EntryRequest[]>([]);
  const [loading, setLoading] = useState(true);
  // 一度でも読み終えたか。読み終える前に「0件」と出すと、実際はデータがあるのに
  // 「無い」と誤解させてしまう（三浦さん指摘 2026-08-29）
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const formUrl = `${window.location.origin}/?entry=1`;

  const load = async () => {
    setLoading(true);
    const r = await fetchEntryRequests();
    setLoading(false);
    if (!r.ok) {
      setError(r.error || "履歴の取得に失敗しました");
      return;
    }
    setError(null);
    const list = r.requests || [];
    setItems(list);
    setLoadedOnce(true);
    // 承認フローがあった時期の未処理が残っていれば知らせる（通常は 0）
    onCount(list.filter((x) => x.status === "pending").length);
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const leftover = items.filter((x) => x.status === "pending");

  return (
    <div className="col gap-16">
      <div className="card" style={{ padding: 14 }}>
        <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
          <div className="col gap-4" style={{ flex: 1, minWidth: 240 }}>
            <div className="card-title">携帯用フォームのURL</div>
            <div className="tiny muted">
              <b>ログインが必要です</b>（メールに届くリンクを1回押すと30日間有効）。徳永さんに配布し、
              携帯のホーム画面に追加してお使いいただけます。送信すると<b>その場でマスターシートに登録</b>され、
              下の履歴に残ります。
            </div>
            <code className="tiny" style={{ wordBreak: "break-all" }}>
              {formUrl}
            </code>
          </div>
          <button
            className="btn"
            onClick={() => {
              // http 環境や権限設定で clipboard が使えないことがある。黙って無反応にしない
              void navigator.clipboard
                ?.writeText(formUrl)
                .then(() => {
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 2000);
                })
                .catch(() => window.prompt("このURLをコピーしてください", formUrl));
            }}
          >
            <Ic.file size={13} /> {copied ? "コピーしました" : "URLをコピー"}
          </button>
          <button className="btn ghost" onClick={() => void load()} disabled={loading}>
            <Ic.history size={13} /> {loading ? "読み込み中…" : "更新"}
          </button>
        </div>
      </div>

      {error && (
        <div className="toast warn">
          <Ic.alert size={14} /> {error}
        </div>
      )}

      {leftover.length > 0 && (
        <div className="toast warn">
          <Ic.alert size={14} /> 承認待ちのまま残っている届出が {leftover.length} 件あります（承認方式だった頃のものです）。
          内容を確認し、必要ならマスター編集から手動で登録してください。
        </div>
      )}

      <div className="card" style={{ padding: 14 }}>
        <div className="row gap-8" style={{ alignItems: "center", marginBottom: 8 }}>
          <div className="card-title">
            携帯から登録された履歴{loadedOnce && !loading ? `（${items.length}件）` : ""}
          </div>
          {loading && (
            <span className="tiny muted row gap-6" style={{ alignItems: "center" }}>
              <Spinner /> 読み込み中…
            </span>
          )}
        </div>
        {loading && !loadedOnce && <SkeletonRows />}
        {loadedOnce && !loading && items.length === 0 && (
          <div className="tiny muted">まだありません。上のURLを携帯で開いて送信すると、ここに残ります。</div>
        )}
        <div className="col gap-12">
          {items.map((it) => (
            <div key={it.id} style={{ border: "1px solid var(--border)", borderRadius: 8, padding: "12px 14px" }}>
              <div className="row gap-8" style={{ alignItems: "center", flexWrap: "wrap" }}>
                <span className={"chip " + (it.kind === "join" ? "green" : "blue")}>
                  {it.kind === "join" ? "入社" : "契約解除"}
                </span>
                <b>{it.kind === "join" ? it.join?.name : it.leave?.name}</b>
                {it.empId && <span className="tiny muted">{it.empId}</span>}
                <span className="tiny muted">
                  {new Date(it.createdAt).toLocaleString("ja-JP")}
                  {it.submittedBy ? `／${it.submittedBy}` : ""}
                </span>
                {it.status === "pending" && <span className="chip amber">未処理</span>}
              </div>

              {it.kind === "join" && it.join && (
                <div className="tiny col gap-2" style={{ marginTop: 8 }}>
                  <div>
                    所属: {it.join.org || "個人"}　営業所: {it.join.branch || "—"}　メール: {it.join.email || "—"}
                  </div>
                  <div>
                    契約開始日: {it.join.contractFrom || "—"}　登録番号: {it.join.invoiceNo || "未登録"}
                  </div>
                  <div>
                    単価 宅急便 {it.join.unitTakkyu ?? "—"} / ネコポス {it.join.unitNekopos ?? "—"} / DM {it.join.unitDm ?? "—"}
                  </div>
                  <div>
                    控除 携帯 {it.join.monthlyKeitai ?? "—"} / 車両 {it.join.monthlySharyo ?? "—"} / アプリ{" "}
                    {it.join.monthlyAppli ?? "—"} / 指導料 {it.join.shidoCount ?? "—"}件
                  </div>
                  <div>
                    ガソリンカード: {it.join.hasGasCard ? "貸与" : "—"}　契約書類:{" "}
                    {it.join.contractDocOk ? "受領済み" : "—"}　AT-STYLE様所属: {it.join.atStyle ? "はい" : "—"}
                  </div>
                  {it.join.notes && <div>備考: {it.join.notes}</div>}
                </div>
              )}

              {it.kind === "leave" && it.leave && (
                <div className="tiny" style={{ marginTop: 8 }}>契約終了日: {it.leave.contractTo}</div>
              )}

              {it.note && <div className="tiny muted" style={{ marginTop: 6 }}>— {it.note}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
